import PropTypes from 'prop-types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Bell, User, RefreshCw, LogOut } from 'lucide-react'

import { useAuthStore } from '@caradasstore/AuthStore'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useOrganisationStore } from '@caradasstore/OrganisationStore'
import { useUserStore } from '@caradasstore/UserStore'
import { useAvailabilityStore } from '@caradasstore/AvailabilityStore'
import useAdasIntelligenceStore from '@caradasstore/AdasIntelligenceStore'
import { useEntitlementStore } from '@caradasstore/EntitlementStore'
import { CaradasSnackbar } from '@components/snackbar'
import { PackageBadge } from '../billing/PackageBadge'
import { UserProfile } from '@components/user'
import { AdasBox, AdasDrawer, AdasPaper, AdasPopper, AdasTypography } from '@components/wrapper-components'
import { purge } from '../../utils/persist'
import { clearCachedCalibrationCenters } from '../../utils/calibrationCenterCache'
import { resetCenterListBootstrap } from '../../utils/centerListBootstrap'

const getInitials = (name) => {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return parts[0][0]?.toUpperCase() || '?'
}

export const CaradasAppBar = ({ title, sx }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const authCtx = useAuthStore()
  const [isProfileOpen, setIsProfileOpen] = useState(false)
  const history = useHistory()
  const currentUser = useUserStore((store) => store.currentUser)
  const userEmail = useUserStore((store) => store.userEmail)
  const centerDetail = useCenterStore((store) => store.centerDetail)

  // Surface the active calibration center on every screen (replaces page-path breadcrumbs).
  const breadcrumb = centerDetail?.name || title || ''

  const handleLogOut = useCallback(() => {
    authCtx.logout()
    // Reset every persisted in-memory store before we purge localStorage so the
    // next user lands on a clean slate (otherwise the previous account's cached
    // calibration centers, assignee list, org, availability, AI filters, etc.
    // would still be visible until a hard reload).
    useCenterStore.getState().reset?.()
    useUserStore.getState().reset?.()
    useOrganisationStore.getState().reset?.()
    useAvailabilityStore.getState().reset?.()
    useAdasIntelligenceStore.getState().reset?.()
    // Entitlements are per user (plan narrowed by role): the next account must not inherit them.
    useEntitlementStore.getState().reset?.()
    purge()
    clearCachedCalibrationCenters()
    // Logout is an SPA navigation (no reload), so clear the module-level
    // bootstrap guard or the next login skips the full center-list fetch.
    resetCenterListBootstrap()
    // Zustand's official `persist` middleware (used by AuthStore) writes to its
    // own localStorage key, which `purge()` from zustand-persist doesn't touch.
    try {
      localStorage.removeItem('auth-state')
    } catch {
      /* ignore */
    }
    history.push('/authenticate')
  }, [authCtx, history])

  const handleOpen = useCallback((evt) => {
    setAnchorEl((prev) => (prev ? null : evt.currentTarget))
  }, [])

  const popoverRef = useRef(null)
  const open = Boolean(anchorEl)

  const handleClose = () => {
    setAnchorEl(null)
  }

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        handleClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const handleIsProfileOpen = () => {
    setIsProfileOpen(true)
  }

  return (
    <AdasBox style={{ background: 'var(--primary-gradient)' }}>
      <AdasBox className='flex items-center justify-between w-full py-2.5 px-6 border-b border-white/10'>
        <AdasTypography className='min-w-0 truncate text-[13px] font-normal'>
          {breadcrumb.split(' / ').map((seg, i, arr) => (
            <span key={i}>
              {i > 0 && <span className='mx-1.5 text-white/40'>/</span>}
              <span className={i === arr.length - 1 ? 'text-white' : 'text-white/60'}>{seg}</span>
            </span>
          ))}
        </AdasTypography>
        <AdasBox className='flex shrink-0 items-center gap-4'>
          {/* The active center's package (plan, trial countdown, or why it is locked). */}
          <PackageBadge />
          <button className='p-1.5 rounded-full hover:bg-white/10 transition-colors relative' title='Notifications'>
            <Bell className='w-5 h-5 text-white/80' />
            <span className='absolute top-1 right-1 w-2 h-2 bg-destructive rounded-full border border-[#0A1628]' />
          </button>
          <AdasBox className='flex items-center cursor-pointer' onClick={handleOpen}>
            <AdasBox className='w-9 h-9 rounded-full bg-white/15 flex items-center justify-center text-sm font-bold text-white'>
              {getInitials(currentUser?.name)}
            </AdasBox>
          </AdasBox>
        </AdasBox>
        <CaradasSnackbar className='ml-0 md:ml-0' />
        <AdasDrawer anchor='right' open={isProfileOpen}>
          <UserProfile user={currentUser} closeDrawer={() => setIsProfileOpen(false)} />
        </AdasDrawer>

        <AdasPopper open={open} anchorEl={anchorEl} placement='bottom-end'>
          <div ref={popoverRef}>
            <AdasPaper className='w-[260px] rounded-lg shadow-overlay p-0 overflow-hidden'>
              <AdasBox className='px-4 py-3'>
                <AdasTypography className='text-[15px] font-semibold text-foreground'>{currentUser?.name || 'User'}</AdasTypography>
                <AdasTypography className='text-[13px] text-muted-foreground mt-0.5'>
                  {userEmail || currentUser?.email || ''}
                </AdasTypography>
              </AdasBox>
              <hr className='border-border' />
              <AdasBox className='py-1'>
                <button
                  onClick={() => {
                    handleClose()
                    handleIsProfileOpen()
                  }}
                  className='flex items-center gap-3 w-full px-4 py-2.5 text-[14px] text-foreground hover:bg-muted transition-colors'
                >
                  <User className='w-[18px] h-[18px] text-muted-foreground' />
                  User Profile
                </button>
                <button
                  onClick={() => {
                    handleClose()
                    window.location.reload()
                  }}
                  className='flex items-center gap-3 w-full px-4 py-2.5 text-[14px] text-foreground hover:bg-muted transition-colors'
                >
                  <RefreshCw className='w-[18px] h-[18px] text-muted-foreground' />
                  Refresh
                </button>
              </AdasBox>
              <hr className='border-border' />
              <AdasBox className='py-1'>
                <button
                  onClick={() => {
                    handleClose()
                    handleLogOut()
                  }}
                  className='flex items-center gap-3 w-full px-4 py-2.5 text-[14px] text-destructive hover:bg-muted transition-colors'
                >
                  <LogOut className='w-[18px] h-[18px]' />
                  Sign Out
                </button>
              </AdasBox>
            </AdasPaper>
          </div>
        </AdasPopper>
      </AdasBox>
    </AdasBox>
  )
}

CaradasAppBar.propTypes = {
  title: PropTypes.string,
  sx: PropTypes.object
}
